import { useEffect } from 'react';

/**
 * Dependency-free document <head> manager for SEO.
 *
 * This is a CRA single-page app (no SSR), so per-page metadata has to be set
 * on the client. This component imperatively sets the document title plus the
 * description, canonical, Open Graph, and Twitter tags on mount / when its
 * props change. Tags are upserted (reused if already present) so we never
 * accumulate duplicates as the user navigates between routes.
 */

const SITE_NAME = 'Appraisal Delivery';
// Production canonical host (www is the canonical domain; non-www 301s here).
const SITE_URL = 'https://www.appraisaldelivery.com';
const DEFAULT_DESCRIPTION =
  'Connect with licensed, independent real estate appraisers for litigation, estate, tax, divorce, and insurance valuations on Appraisal Delivery.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo-blue.png`;

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function Seo({ title, description, path, image, noIndex = false }) {
  useEffect(() => {
    const fullTitle = !title
      ? SITE_NAME
      : title.includes(SITE_NAME)
        ? title
        : `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    const desc = description || DEFAULT_DESCRIPTION;
    const url = `${SITE_URL}${path || window.location.pathname}`;
    const ogImage = image || DEFAULT_OG_IMAGE;

    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', ogImage);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', ogImage);
  }, [title, description, path, image, noIndex]);

  return null;
}
